// Forms (DESIGN §4): right-side Drawer sheets that ask the minimum questions — Add/Edit Batch, New/Edit client,
// Event (full form), Team member. Rules shared by every sheet:
// - Enter submits; the first invalid field gets focus and its error is its accessible description.
// - Edit forms send ONLY the fields the user changed, so edits made elsewhere in the meantime survive.
// - A stray backdrop click never discards typed input; Esc / ✕ / Cancel on a dirty form ask "Discard your changes?"
//   inline first. When an action fails, dispatch() has already toasted the reason and the sheet stays open.
import './forms.css';
import { cloneElement, toChildArray } from 'preact';
import { useEffect, useId, useRef, useState } from 'preact/hooks';
import {
  CLIENT_STATUSES, DEFAULT_VIDEO_COUNT, DEPARTMENTS, EVENT_KINDS, LOGIN_ID_PATTERN, MAX_BATCH, MAX_MONTH, MAX_VIDEO_COUNT, PASSWORD_MAX,
  PASSWORD_MIN, STAGES, TEAM_ROLES, batchLabel, loginSlug, monthLabel, plural,
} from '../../shared/constants.js';
import { addDays, datePart, formatDate, isISODate, monthRange, timePart } from '../../shared/dates.js';
import { sanitizeUrl } from '../../shared/core.js';
import { activeMonthNumber, batchNumberTaken, nextBatchNumber } from '../../shared/derive.js';
import { MODE, dispatch, getSnapshot, useStore } from '../store.js';
import { getRoute, navigate, clientHref } from '../router.js';
import { Icon } from '../icons.jsx';
import { Button, DateInput, IconButton, Select, TextArea, TextInput, cx, toast, useDialog } from './ui.jsx';
import { openDrawer } from './drawer.jsx';
import { removalText } from '../views/people-removal.js';
import { FilterButtons } from './filter-buttons.jsx';
import { RangeField, RangePair } from './range-field.jsx';
import { ChoiceSelect, choiceOptions } from './choice-select.jsx';
import {
  DEFAULTS, LIMITS, WEEKDAYS, cleanHandle, contentPerMonthOf, contractBatchesOf, contractMonthsOf, handleValid, picOf, postingDaysOf,
} from '../contract.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX = { name: 120, contact: 120, email: 200, phone: 200, notes: 5000, url: 2000 };
const CALENDAR_OPTIONS = [
  { value: 'main', label: 'Main Calendar' },
  { value: 'internal', label: 'Internal Notes' },
];

const byName = (a, b) => a.name.localeCompare(b.name);
const norm = (v) => (typeof v === 'string' ? v.trim() : v ?? '');

// Same rules as the reducer (sanitizeUrl), surfaced inline before dispatching.
function urlError(value) {
  try {
    sanitizeUrl(value);
    return '';
  } catch (err) {
    return err.message || 'Enter a valid link.';
  }
}

// ⌘/Ctrl + Enter submits from a textarea (plain Enter adds a line there).
function submitOnModEnter(e) {
  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
    e.preventDefault();
    e.currentTarget.form?.requestSubmit();
  }
}

// ---------- exported helpers (reusable by views) ----------

// Team members as Select options, sorted by name.
export function teamOptions(team = []) {
  return [...team].sort(byName).map((t) => ({ value: t.id, label: t.name }));
}

// Owner choices: active members only (an inactive one cannot sign in), plus the one already chosen.
export const activeTeamOptions = (team = [], keep = '') => teamOptions(team.filter((t) => t.active !== false || (keep && t.id === keep)));

// Saved, but the batch number is used twice for the client (not blocking, DESIGN §7): say so louder than a success toast.
function duplicateToast(label, saved) {
  toast(`${saved} ${label} already exists for this client — change the number in Edit.`, { tone: 'warning', duration: 7000 });
}

// Default due date for a new batch: the end of its month (1 batch = 1 month of production); without a month, today + 7.
export function defaultDueDate(month, today) {
  return month?.endDate || addDays(today, 7);
}

// Month no. for a client's next batch: the first month from `from` on that holds no batch and is not off
// (1 batch = 1 month, so the current month already holding Batch 01 suggests the next free one).
function nextFreeMonth(state, index, clientId, from) {
  const used = new Set();
  for (const m of index.monthsByClient.get(clientId) || []) if (m.off) used.add(m.monthNumber);
  for (const b of state.batches) {
    if (b.clientId === clientId) used.add(index.monthsById.get(b.monthId)?.monthNumber);
  }
  let n = Math.max(1, from);
  while (used.has(n) && n < MAX_MONTH) n += 1;
  return n;
}

// Owner for a client's next batch: whoever owns its latest batch (the same editor usually continues), else `fallback`.
function lastOwnerOf(state, index, clientId, fallback) {
  const latest = state.batches.filter((b) => b.clientId === clientId && index.teamById.get(b.owner)?.active !== false)
    .sort((a, b) => (b.batchNumber - a.batchNumber) || (b.createdAt || '').localeCompare(a.createdAt || ''))[0];
  return latest?.owner && index.teamById.has(latest.owner) ? latest.owner : fallback;
}

// { startDate, endDate } of Month n of a client (stored month, else computed from the start date).
function monthSpan(index, client, n) {
  if (!client || !n) return null;
  const stored = (index.monthsByClient.get(client.id) || []).find((m) => m.monthNumber === n);
  return stored || monthRange(client.startDate, n);
}

// The month of `clientId` that contains today (Month 01–99).
export function activeMonthOf(index, clientId, today) {
  const client = index.clientsById.get(clientId);
  if (!client) return null;
  const n = activeMonthNumber(client, today);
  return (index.monthsByClient.get(clientId) || []).find((m) => m.monthNumber === n) || null;
}

// { key: value } for the keys whose (trimmed) value differs from `initial` — the patch of an edit form.
export function changedFields(initial, values, keys = Object.keys(values)) {
  const patch = {};
  for (const k of keys) if (norm(values[k]) !== norm(initial[k])) patch[k] = values[k];
  return patch;
}

// ---------- form state ----------

function useForm(init) {
  const [initial] = useState(init);
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (patch) => {
    setValues((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(patch)) delete next[k];
      return next;
    });
  };
  const bind = (key) => ({ name: key, value: values[key], invalid: !!errors[key], onValue: (v) => set({ [key]: v }) });
  const dirty = Object.keys(changedFields(initial, values)).length > 0;

  // Validates; with errors, focuses the first invalid control in document order. Otherwise runs `action(values)`
  // (→ Promise) with the pending state on; a rejection re-enables the form and keeps the sheet open.
  const submit = (e, validate, action) => {
    if (busy) return;
    const found = validate(values);
    const keys = Object.keys(found).filter((k) => found[k]);
    setErrors(found);
    if (keys.length) {
      const form = e?.currentTarget?.closest ? e.currentTarget.closest('form') || e.currentTarget : null;
      form?.querySelector?.(keys.map((k) => `[name="${k}"]`).join(', '))?.focus();
      return;
    }
    setBusy(true);
    let pending;
    try {
      pending = Promise.resolve(action(values));
    } catch (err) {
      pending = Promise.reject(err);
    }
    pending.catch(() => setBusy(false));
  };

  return { initial, values, set, bind, errors, busy, setBusy, submit, dirty };
}

// ---------- shared pieces ----------

const CONTROLS = new Set([TextInput, TextArea, DateInput, Select]);

// Labelled field. The first control child gets aria-describedby (its error, else its hint) and aria-required, so a
// screen reader announces "Client Name, required, invalid, Enter the client name." when focus lands on it.
function Field({ label, hint, error, required, as: Tag = 'label', class: cls, children }) {
  const id = useId();
  const msgId = error || hint ? `${id}-msg` : undefined;
  let done = false;
  const kids = toChildArray(children).map((c) => {
    if (done || !c || typeof c !== 'object' || !(CONTROLS.has(c.type) || c.type === Pick)) return c;
    done = true;
    return cloneElement(c, { 'aria-describedby': msgId, 'aria-required': required ? 'true' : undefined });
  });
  return (
    <Tag class={cx('field', cls)}>
      {label && (
        <span class="field-label">
          {label}
          {required && <span class="field-required" aria-hidden="true">*</span>}
        </span>
      )}
      {kids}
      {error ? <span class="field-error" id={msgId}>{error}</span> : hint && <span class="field-hint" id={msgId}>{hint}</span>}
    </Tag>
  );
}

// Native select whose empty placeholder reads as a placeholder (muted), not as a chosen value.
function Pick(props) {
  return <Select {...props} class={cx(props.class, (props.value ?? '') === '' && 'is-empty')} />;
}

// "More options" disclosure for optional fields.
function MoreOptions({ open, onToggle, label = 'More options', children }) {
  const id = useId();
  return (
    <div class={cx('fm-more', open && 'is-open')}>
      <button type="button" class="fm-more-toggle" aria-expanded={open ? 'true' : 'false'} aria-controls={id} onClick={onToggle}>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={14} />
        {label}
      </button>
      <div id={id} class="fm-more-body" hidden={!open}>{children}</div>
    </div>
  );
}

// Footer: [Delete …] · Cancel · primary. "Delete" swaps the footer into an inline confirmation (Esc steps back).
function SheetFooter({ busy, submitLabel, busyLabel, onCancel, remove }) {
  const [armed, setArmed] = useState(false);
  const confirmRef = useRef(null);
  const triggerRef = useRef(null);
  const wasArmed = useRef(false);
  useEffect(() => {
    if (armed) confirmRef.current?.focus();
    else if (wasArmed.current) triggerRef.current?.focus();
    wasArmed.current = armed;
  }, [armed]);

  if (armed) {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setArmed(false);
    };
    return (
      <div class="fm-footer is-confirming" onKeyDown={onKeyDown}>
        <p class="fm-confirm-text" role="alert">{remove.prompt}</p>
        <Button variant="ghost" onClick={() => setArmed(false)}>Keep</Button>
        <button ref={confirmRef} type="button" class="btn btn-danger" disabled={busy} onClick={remove.run}>
          {busy ? 'Deleting…' : remove.label}
        </button>
      </div>
    );
  }
  return (
    <div class="fm-footer">
      {remove && (
        <button ref={triggerRef} type="button" class="btn btn-ghost fm-delete" onClick={() => setArmed(true)}>
          <Icon name="trash" size={15} />
          {remove.label}
        </button>
      )}
      <span class="spacer" />
      <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      <Button variant="primary" type="submit" disabled={busy}>{busy ? busyLabel : submitLabel}</Button>
    </div>
  );
}

// The Drawer (drawer.jsx classes) as a <form>, with the dirty guard wired to the form's own diff too (dropdowns and
// button groups don't fire input events). `dirty` = the values differ from how the sheet opened.
function FormSheet({ title, subtitle, onClose, onSubmit, dirty, width = 420, footer, class: cls, children }) {
  const { dialogRef, trapTab, backdropProps, markDirty, requestClose, discardBar } = useDialog(onClose);
  const titleId = useId();
  useEffect(() => {
    if (dirty && dialogRef.current) markDirty({ target: dialogRef.current });
  }, [dirty]);
  const submit = (e) => {
    e.preventDefault();
    onSubmit(e);
  };
  return (
    <div class="drawer-backdrop" {...backdropProps}>
      <form
        ref={dialogRef}
        class={cx('drawer', 'fm-sheet', cls)}
        style={{ '--drawer-w': `${width}px` }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={trapTab}
        onInput={markDirty}
        onChange={markDirty}
        onSubmit={submit}
        noValidate
      >
        <div class="drawer-header">
          <div class="drawer-heading">
            <h2 class="drawer-title" id={titleId}>{title}</h2>
            {subtitle && <p class="meta fm-subtitle">{subtitle}</p>}
          </div>
          <IconButton icon="x" label="Close" size="sm" onClick={requestClose} />
        </div>
        <div class="drawer-body fm-body">{children}</div>
        {discardBar}
        <div class="drawer-footer">{footer(requestClose)}</div>
      </form>
    </div>
  );
}

// Keeps an edit form from outliving its record (deleted elsewhere, or by this form).
function useCloseWhenMissing(editing, entity, close) {
  useEffect(() => {
    if (editing && !entity) close();
  }, [editing, entity]);
}

// ---------- client (DESIGN §9) ----------

// Plain fields the edit form diffs one by one; postingDays / pic / numbers are compared in their own way below.
const CLIENT_TEXT_KEYS = ['name', 'instagram', 'tiktok', 'status', 'startDate', 'owner', 'contactName', 'email', 'phone', 'website', 'folderUrl', 'notes'];
const OPTIONAL_CLIENT_KEYS = ['contactName', 'email', 'phone', 'website', 'folderUrl', 'notes'];
const PIC_ROLES = [
  { key: 'script', field: 'picScript', label: 'Script PIC' },
  { key: 'videographer', field: 'picVideographer', label: 'Videographer PIC' },
  { key: 'editor', field: 'picEditor', label: 'Editor PIC' },
];
const [CONTENT_MIN, CONTENT_MAX] = LIMITS.content;

// 'mon,wed,fri' ⇄ ['mon','wed','fri'] (the form keeps a canonical string so the dirty/changed checks stay simple).
const daysKey = (days) => WEEKDAYS.filter((d) => days?.includes(d.key)).map((d) => d.key).join(',');
const daysOfKey = (key) => (key ? key.split(',') : []);

function clientFootprint(state, index, clientId) {
  const batches = index.batchesByClient.get(clientId) || [];
  const batchIds = new Set(batches.map((b) => b.id));
  const events = state.events.filter((e) => e.clientId === clientId || batchIds.has(e.batchId)).length;
  return { batches: batches.length, events, months: (index.monthsByClient.get(clientId) || []).length };
}

function clientInitial(client, index, currentPerson, today) {
  const member = (id) => (id && index?.teamById.has(id) ? id : '');
  if (client) {
    const pic = picOf(client);
    const init = {
      contractBatches: contractBatchesOf(client), contractMonths: contractMonthsOf(client),
      contentPerMonth: String(contentPerMonthOf(client)), postingDays: daysKey(postingDaysOf(client)),
      owner: member(client.owner), status: client.status || 'Active',
    };
    for (const r of PIC_ROLES) init[r.field] = member(pic[r.key]);
    for (const k of CLIENT_TEXT_KEYS) if (!(k in init)) init[k] = client[k] || '';
    return init;
  }
  return {
    name: '', instagram: '', tiktok: '', status: 'Active', startDate: today,
    contractBatches: DEFAULTS.batches, contractMonths: DEFAULTS.months, contentPerMonth: String(DEFAULTS.content), postingDays: '',
    owner: member(currentPerson), picScript: '', picVideographer: '', picEditor: '', startMonthOne: true,
    contactName: '', email: '', phone: '', website: '', folderUrl: '', notes: '',
  };
}

function validateClient(x, { ownerRequired }) {
  const e = {};
  if (!x.name.trim()) e.name = 'Enter the client name.';
  for (const k of ['instagram', 'tiktok']) if (!handleValid(cleanHandle(x[k]))) e[k] = 'Use letters, numbers, dots and underscores.';
  if (!x.startDate) e.startDate = 'Choose a start date.';
  else if (!isISODate(x.startDate)) e.startDate = 'Enter a valid date.';
  const content = Number(String(x.contentPerMonth).trim());
  if (!Number.isInteger(content) || content < CONTENT_MIN || content > CONTENT_MAX) e.contentPerMonth = `Enter a number from ${CONTENT_MIN} to ${CONTENT_MAX}.`;
  if (!x.owner && ownerRequired) e.owner = 'Choose an account owner.';
  if (x.email.trim() && !EMAIL_RE.test(x.email.trim())) e.email = 'Enter a valid email address.';
  const web = urlError(x.website);
  if (web) e.website = web;
  const folder = urlError(x.folderUrl);
  if (folder) e.folderUrl = folder;
  return e;
}

// The stored shape of the form's values (handles without "@", numbers as numbers, days as an array).
function clientFields(x) {
  const out = {};
  for (const k of CLIENT_TEXT_KEYS) out[k] = typeof x[k] === 'string' ? x[k].trim() : x[k];
  out.instagram = cleanHandle(x.instagram);
  out.tiktok = cleanHandle(x.tiktok);
  out.contractBatches = x.contractBatches;
  out.contractMonths = x.contractMonths;
  out.contentPerMonth = Number(String(x.contentPerMonth).trim());
  out.postingDays = daysOfKey(x.postingDays);
  out.pic = Object.fromEntries(PIC_ROLES.map((r) => [r.key, x[r.field] || '']));
  return out;
}

// Edit: only what the user changed (`pic` only with the changed roles — the reducer keeps the other two).
function clientPatch(initial, x) {
  const a = clientFields(initial);
  const b = clientFields(x);
  const patch = {};
  for (const k of [...CLIENT_TEXT_KEYS, 'contractBatches', 'contractMonths', 'contentPerMonth']) if (a[k] !== b[k]) patch[k] = b[k];
  if (daysKey(a.postingDays) !== daysKey(b.postingDays)) patch.postingDays = b.postingDays;
  const roles = PIC_ROLES.filter((r) => a.pic[r.key] !== b.pic[r.key]);
  if (roles.length) patch.pic = Object.fromEntries(roles.map((r) => [r.key, b.pic[r.key]]));
  return patch;
}

function ClientForm({ close, clientId, onCreated }) {
  const { state, index, today, currentPerson } = useStore();
  const editing = !!clientId;
  const client = editing ? index?.clientsById.get(clientId) : null;
  const form = useForm(() => clientInitial(client, index, currentPerson, today));
  const [more, setMore] = useState(() => editing && OPTIONAL_CLIENT_KEYS.some((k) => client?.[k]));
  const switchTouched = useRef(false);
  useCloseWhenMissing(editing, client, close);
  if (!state || (editing && !client)) return null;

  const { values: v, bind, errors, busy } = form;
  // A client whose owner left the team may stay unassigned; everyone else needs an owner.
  const ownerRequired = !editing || !!client.owner;
  const trimmed = v.name.trim().toLowerCase();
  const duplicate = trimmed && state.clients.some((c) => c.id !== clientId && c.name.trim().toLowerCase() === trimmed);
  const content = Number(String(v.contentPerMonth).trim());
  const reels = Number.isInteger(content) && content >= CONTENT_MIN && content <= CONTENT_MAX ? content : DEFAULTS.content;

  const validate = (x) => {
    const e = validateClient(x, { ownerRequired });
    if ((e.email || e.website || e.folderUrl) && !more) setMore(true);
    return e;
  };

  const onSubmit = (e) => form.submit(e, validate, (x) => {
    if (editing) {
      const patch = clientPatch(form.initial, x);
      if (!Object.keys(patch).length) return close();
      return dispatch('client.update', { id: client.id, patch }).then((result) => {
        const moved = result?.moved ? ` ${plural(result.moved, 'batch', 'batches')} moved to their new month.` : '';
        toast(`${x.name.trim()} saved.${moved}`, { tone: 'success' });
        close();
      });
    }
    // Start Month 1 now: the reducer also creates Batch 01 in Month 01 (result.batchId).
    return dispatch('client.create', { ...clientFields(x), startMonthOne: !!x.startMonthOne }).then((result) => {
      const id = result?.clientId;
      toast(result?.batchId ? 'Client created with Batch 01.' : 'Client created.', { tone: 'success' });
      close();
      if (onCreated) onCreated(id, result);
      else if (id) navigate(clientHref(id).replace(/^#/, ''));
    });
  });

  const onDelete = () => {
    form.setBusy(true);
    const { name, id } = client;
    dispatch('client.delete', { id })
      .then(() => {
        toast(`${name} deleted.`);
        close();
        // Replace, so Back does not land on the deleted client's workspace.
        const route = getRoute();
        if (route.name === 'client' && route.params.clientId === id) navigate('/clients', { replace: true });
        else if (route.name === 'workspace' && route.params.clientId === id) navigate('/workspace', { replace: true });
      })
      .catch(() => form.setBusy(false));
  };

  let remove;
  if (editing) {
    const f = clientFootprint(state, index, client.id);
    remove = {
      label: 'Delete client',
      prompt: `Deletes ${plural(f.months, 'month')}, ${plural(f.batches, 'batch', 'batches')} and ${plural(f.events, 'event')}. This can’t be undone.`,
      run: onDelete,
    };
  }

  const startHint = editing && v.startDate !== client.startDate && isISODate(v.startDate)
    ? 'Every month shifts to the new start date; batches stay in their months.'
    : 'Month 01 starts here.';
  const team = teamOptions(state.team.filter((t) => t.active !== false || Object.values(v).includes(t.id)));
  const days = daysOfKey(v.postingDays);
  const toggleDay = (key) => form.set({ postingDays: daysKey(days.includes(key) ? days.filter((d) => d !== key) : [...days, key]) });
  const firstOwner = index.teamById.get(v.picScript || v.owner)?.name;
  // An existing client onboarded with an older start date: Month 01 is already over (ROLES-04).
  const monthOneOver = (date) => !!date && isISODate(date) && monthRange(date, 1).endDate < today;
  const monthOneEnd = monthOneOver(v.startDate) ? monthRange(v.startDate, 1).endDate : '';
  const onStartDate = (date) => form.set(editing || switchTouched.current ? { startDate: date } : { startDate: date, startMonthOne: !monthOneOver(date) });
  const switchHint = !v.startMonthOne ? 'Add the first batch later.'
    : `${monthOneEnd ? `Month 01 ended ${formatDate(monthOneEnd, monthOneEnd.slice(0, 4) === today.slice(0, 4) ? 'short' : 'long')}: Batch 01 gets no deadline · ` : 'Creates Batch 01 in Month 01 · '}${plural(reels, 'video')}${firstOwner ? ` · ${firstOwner}` : ''}.`;

  return (
    <FormSheet
      title={editing ? 'Edit client' : 'Add client'}
      subtitle={editing ? undefined : 'Batches and months are counted separately. Month 1 can be started right here.'}
      class="fm-client"
      width={480}
      onClose={close}
      onSubmit={onSubmit}
      dirty={form.dirty}
      footer={(cancel) => (
        <SheetFooter onCancel={cancel} busy={busy} remove={remove} submitLabel={editing ? 'Save' : 'Create'} busyLabel={editing ? 'Saving…' : 'Creating…'} />
      )}
    >
      <FormSection title="Client">
        <Field label="Client name" required error={errors.name} hint={duplicate ? 'A client with this name already exists.' : undefined}>
          <TextInput {...bind('name')} maxLength={MAX.name} placeholder="e.g. Bryant Frazier Agency" autoComplete="off" data-autofocus />
        </Field>
        <div class="fm-grid fm-grid-keep">
          <Field label="Instagram" error={errors.instagram}>
            <TextInput {...bind('instagram')} maxLength={80} placeholder="@clienthandle" autoComplete="off" autoCapitalize="off" spellcheck={false} />
          </Field>
          <Field label="TikTok" error={errors.tiktok}>
            <TextInput {...bind('tiktok')} maxLength={80} placeholder="@clienthandle" autoComplete="off" autoCapitalize="off" spellcheck={false} />
          </Field>
        </div>
        {!errors.instagram && !errors.tiktok && <p class="field-hint fm-pair-hint">With or without the @.</p>}
        <div class="fm-grid">
          <Field label="Status">
            <Select {...bind('status')} options={CLIENT_STATUSES} />
          </Field>
          <Field label="Start date" required error={errors.startDate} hint={startHint}>
            <DateInput {...bind('startDate')} onValue={onStartDate} />
          </Field>
        </div>
      </FormSection>

      <FormSection title="Contract">
        <RangePair>
          <RangeField label="Batch count" name="contractBatches" value={v.contractBatches} onValue={(n) => form.set({ contractBatches: n })}
            min={LIMITS.batches[0]} max={LIMITS.batches[1]} unit={['batch', 'batches']}
            helper={`Contracted production runs. 1 batch = 1 production run = ${reels} reels.`} />
          <RangeField label="Month count" name="contractMonths" value={v.contractMonths} onValue={(n) => form.set({ contractMonths: n })}
            min={LIMITS.months[0]} max={LIMITS.months[1]} unit={['month', 'months']}
            helper="Contracted months. Counted separately from batches: a paused month stretches this without adding a batch." />
        </RangePair>
        <Field label="Content per month" required error={errors.contentPerMonth} hint={`Reels per month (${CONTENT_MIN}–${CONTENT_MAX}).`}>
          <TextInput {...bind('contentPerMonth')} class="fm-videos" inputMode="numeric" maxLength={3} autoComplete="off" />
        </Field>
        <Field as="div" label="Posting days" hint="Reel posting slots are scheduled on these days.">
          <div class="fm-days" role="group" aria-label="Posting days">
            {WEEKDAYS.map((d) => (
              <label key={d.key} class={cx('fm-day', days.includes(d.key) && 'is-on')}>
                <input type="checkbox" name={`posting-${d.key}`} checked={days.includes(d.key)} onChange={() => toggleDay(d.key)} />
                {d.label}
              </label>
            ))}
          </div>
        </Field>
      </FormSection>

      <FormSection title="Team (PIC)">
        <div class="fm-grid fm-grid-keep">
          <Field label="Account owner" required={ownerRequired} error={errors.owner}>
            <Pick {...bind('owner')} options={team} placeholder={state.team.length ? (ownerRequired ? 'Choose' : 'Unassigned') : 'No team members yet'} />
          </Field>
          {PIC_ROLES.map((r) => (
            <Field key={r.key} label={r.label}>
              <Pick {...bind(r.field)} options={team} placeholder="None" />
            </Field>
          ))}
        </div>
        {!state.team.length && <TeamHint onCreated={(id) => form.set({ owner: id })} />}
      </FormSection>

      {!editing && (
        <label class="fm-switch">
          <input type="checkbox" role="switch" name="startMonthOne" checked={!!v.startMonthOne} onChange={(e) => { switchTouched.current = true; form.set({ startMonthOne: e.currentTarget.checked }); }} />
          <span class="fm-switch-text">
            <span class="fm-switch-label">Start Month 1 now</span>
            <span class={cx('field-hint', v.startMonthOne && monthOneEnd && 'fm-warn')}>{switchHint}</span>
          </span>
        </label>
      )}

      <MoreOptions open={more} onToggle={() => setMore(!more)}>
        <Field label="Contact name">
          <TextInput {...bind('contactName')} maxLength={MAX.contact} placeholder="Full name" autoComplete="off" />
        </Field>
        <div class="fm-grid">
          <Field label="Email" error={errors.email}>
            <TextInput {...bind('email')} type="email" inputMode="email" maxLength={MAX.email} placeholder="name@company.com" autoComplete="off" />
          </Field>
          <Field label="Phone">
            <TextInput {...bind('phone')} type="tel" maxLength={MAX.phone} placeholder="+62 812-0000-0000" autoComplete="off" />
          </Field>
        </div>
        <Field label="Website" error={errors.website}>
          <TextInput {...bind('website')} inputMode="url" maxLength={MAX.url} placeholder="company.com" autoComplete="off" />
        </Field>
        <Field label="Client folder" error={errors.folderUrl}>
          <TextInput {...bind('folderUrl')} inputMode="url" maxLength={MAX.url} placeholder="drive.google.com/…" autoComplete="off" />
        </Field>
        <Field label="Notes">
          <TextArea {...bind('notes')} rows={3} maxLength={MAX.notes} placeholder="Tone, approvals, anything the team should know" onKeyDown={submitOnModEnter} />
        </Field>
      </MoreOptions>
    </FormSheet>
  );
}

// A titled group of fields inside a sheet ("Client", "Contract", "Team (PIC)").
function FormSection({ title, children }) {
  const id = useId();
  return (
    <section class="fm-section fm-group" aria-labelledby={id}>
      <h3 class="fm-section-label" id={id}>{title}</h3>
      {children}
    </section>
  );
}

// With an empty team (e.g. right after removing the sample data): add a member inline, then select them.
function TeamHint({ onCreated }) {
  return (
    <p class="fm-note">
      <button type="button" class="fm-inline-action" onClick={() => openTeamMember(null, { onSaved: onCreated })}>
        <Icon name="plus" size={13} />
        Add a team member
      </button>
    </p>
  );
}

// options.onCreated(clientId, result): replaces the default "open the new client's workspace" (result.batchId when
// "Start Month 1 now" already made Batch 01).
export function openAddClient(options) {
  const onCreated = typeof options?.onCreated === 'function' ? options.onCreated : undefined;
  return openDrawer((close) => <ClientForm close={close} onCreated={onCreated} />);
}

// Accepts a client object or its id.
export function openEditClient(client) {
  const id = typeof client === 'string' ? client : client?.id;
  if (!id) return undefined;
  return openDrawer((close) => <ClientForm close={close} clientId={id} />);
}

// ---------- batch ----------

function clientOptionsOf(state) {
  return [...state.clients].sort(byName).map((c) => ({ value: c.id, label: c.status === 'Active' ? c.name : `${c.name} (${c.status})` }));
}

// Batch no. + Month no. sliders and Videos (DESIGN §7.3), shared by Add Batch and Edit Batch.
// Batch numbers are per CLIENT: a number the client already uses gets a small warning (never blocks).
function BatchNumbers({ form, state, index, clientId, exceptId = '', videos = true }) {
  const { values: v, set, bind, errors } = form;
  const taken = clientId && batchNumberTaken(state, clientId, v.batchNumber, exceptId);
  // 1 batch = 1 month: say so when the chosen month already holds another batch (never blocks).
  const month = clientId && (index.monthsByClient.get(clientId) || []).find((m) => m.monthNumber === Number(v.monthNumber));
  const other = month && (index.batchesByMonth.get(month.id) || []).find((b) => b.id !== exceptId);
  const monthNote = other ? `${monthLabel(month.monthNumber)} already has ${batchLabel(other.batchNumber)}.` : month?.off ? `${monthLabel(month.monthNumber)} is marked off.` : '';
  return (
    <>
      <RangePair>
        <RangeField
          label="Batch no."
          name="batchNumber"
          value={v.batchNumber}
          onValue={(n) => set({ batchNumber: n })}
          min={1}
          max={MAX_BATCH}
          unit={['batch', 'batch']}
          helper="1 to 99."
          warning={taken ? `${batchLabel(v.batchNumber)} already exists for this client.` : ''}
        />
        <RangeField
          label="Month no."
          name="monthNumber"
          value={v.monthNumber}
          onValue={(n) => set({ monthNumber: n })}
          min={1}
          max={MAX_MONTH}
          unit={['month', 'months']}
          helper="1 to 99 — not capped by the contract."
          warning={monthNote}
        />
      </RangePair>
      {videos && (
        <Field label="Videos" error={errors.videoCount} class="fm-videos">
          <TextInput {...bind('videoCount')} type="number" min="1" max={String(MAX_VIDEO_COUNT)} step="1" inputMode="numeric" />
        </Field>
      )}
    </>
  );
}

const videosError = (x) => {
  const n = Number(String(x.videoCount).trim());
  return String(x.videoCount).trim() && Number.isInteger(n) && n >= 1 && n <= MAX_VIDEO_COUNT ? '' : `Use 1 to ${MAX_VIDEO_COUNT}.`;
};

// Add Batch: Name · Owner (default: the owner of the client's latest batch, else the current person) · Deadline (the
// month's end) · Batch no. · Month no. · Videos · Client (only without a client context). Defaults: Batch no. = the
// client's highest + 1; Month no. = the context month, else the client's first free month from its current month on
// (no batch, not off) — all follow a client change until the user edits them.
function AddBatchForm({ close, context, onCreated }) {
  const { state, index, today, currentPerson } = useStore();
  const me = currentPerson && index?.teamById.has(currentPerson) ? currentPerson : '';
  const fixedMonth = context.monthId ? index?.monthsById.get(context.monthId) : null;
  const askClient = !context.clientId;
  const autoNumbers = (clientId) => {
    const client = index?.clientsById.get(clientId);
    return {
      batchNumber: client ? nextBatchNumber(state, clientId) : 1,
      monthNumber: fixedMonth ? fixedMonth.monthNumber
        : context.monthNumber || (client ? nextFreeMonth(state, index, clientId, activeMonthNumber(client, today)) : 1),
      owner: client ? lastOwnerOf(state, index, clientId, me) : me,
      // The client's content per month is its batches' default video count (DESIGN §9).
      videoCount: String(client ? Math.min(contentPerMonthOf(client), MAX_VIDEO_COUNT) : DEFAULT_VIDEO_COUNT),
    };
  };
  // The deadline follows Month no. (and the client) until the user edits it.
  const dueFor = (clientId, monthNumber) => defaultDueDate(monthSpan(index, index?.clientsById.get(clientId), monthNumber) || fixedMonth, today);
  const form = useForm(() => {
    const clientId = context.clientId || (state?.clients.length === 1 ? state.clients[0].id : '');
    const auto = autoNumbers(clientId);
    return {
      name: '',
      dueDate: dueFor(clientId, auto.monthNumber),
      clientId,
      ...auto,
    };
  });
  const touched = useRef({});
  if (!state) return null;

  const { values: v, bind, errors, busy } = form;
  const client = index.clientsById.get(v.clientId);
  // Sliders the user has not moved follow the chosen client; an untouched deadline follows the month.
  const withDue = (patch, clientId = v.clientId) => {
    const monthNumber = patch.monthNumber ?? v.monthNumber;
    return touched.current.dueDate || !clientId ? patch : { ...patch, dueDate: dueFor(clientId, monthNumber) };
  };
  const numbers = { ...form, set: (patch) => { Object.keys(patch).forEach((k) => { touched.current[k] = true; }); form.set(withDue(patch)); } };
  const pickClient = (clientId) => {
    const auto = autoNumbers(clientId);
    if (String(v.videoCount).trim() !== autoNumbers(v.clientId).videoCount) touched.current.videoCount = true;
    form.set(withDue({ clientId, ...Object.fromEntries(Object.entries(auto).filter(([k]) => !touched.current[k])) }, clientId));
  };
  const clientField = askClient && (
    <Field label="Client" required error={errors.clientId}>
      <Pick {...bind('clientId')} onValue={pickClient} options={clientOptionsOf(state)} placeholder={state.clients.length ? 'Choose a client' : 'No clients yet'} />
    </Field>
  );

  const validate = (x) => {
    const e = {};
    if (!x.name.trim()) e.name = 'Enter a batch name.';
    if (x.dueDate && !isISODate(x.dueDate)) e.dueDate = 'Enter a valid date.';
    if (!x.clientId) e.clientId = 'Choose a client.';
    e.videoCount = videosError(x);
    return e;
  };

  const onSubmit = (e) => form.submit(e, validate, (x) => {
    const payload = {
      clientId: x.clientId, name: x.name, owner: x.owner, dueDate: x.dueDate,
      batchNumber: x.batchNumber, monthNumber: x.monthNumber, videoCount: Number(String(x.videoCount).trim()),
    };
    return dispatch('batch.create', payload).then((result) => {
      const label = batchLabel(result?.batchNumber || x.batchNumber);
      // Someone else may have taken the number since this form opened (the inline warning only saw this tab's data).
      if (result?.duplicateNumber) duplicateToast(label, `${label} created in ${monthLabel(result?.monthNumber || x.monthNumber)}.`);
      else toast(`${label} created in ${monthLabel(result?.monthNumber || x.monthNumber)}.`, { tone: 'success' });
      close();
      onCreated?.(result);
    });
  });

  return (
    <FormSheet
      title="Add Batch"
      subtitle={!askClient && client ? client.name : undefined}
      width={480}
      onClose={close}
      onSubmit={onSubmit}
      dirty={form.dirty}
      footer={(cancel) => <SheetFooter onCancel={cancel} busy={busy} submitLabel="Create" busyLabel="Creating…" />}
    >
      {clientField}
      <Field label="Batch Name" required error={errors.name}>
        <TextInput {...bind('name')} maxLength={MAX.name} placeholder="e.g. Morning Ritual Series" autoComplete="off" data-autofocus />
      </Field>
      <div class="fm-grid">
        <Field label="Owner">
          <Pick {...bind('owner')} onValue={(o) => { touched.current.owner = true; form.set({ owner: o }); }} options={activeTeamOptions(state.team, v.owner)} placeholder="Unassigned" />
        </Field>
        <Field label="Deadline" error={errors.dueDate} class="fm-due">
          <DateInput {...bind('dueDate')} onValue={(d) => { touched.current.dueDate = true; form.set({ dueDate: d }); }} />
        </Field>
      </div>
      <BatchNumbers form={numbers} state={state} index={index} clientId={v.clientId} />
    </FormSheet>
  );
}

// Edit Batch (batch page ⋯ → Edit): Batch Name · Batch no. · Month no. Owner, Deadline and Videos are edited inline in
// the batch header, notes and links inline on the page, so the sheet never asks twice (and can never revert them).
// Only the changed fields are sent. A new Month no. moves the batch (missing months are created).
function EditBatchForm({ close, batchId }) {
  const { state, index } = useStore();
  const batch = index?.batchesById.get(batchId);
  const form = useForm(() => (batch
    ? {
      name: batch.name,
      batchNumber: batch.batchNumber,
      monthNumber: index.monthsById.get(batch.monthId)?.monthNumber || 1,
    }
    : {}));
  useCloseWhenMissing(true, batch, close);
  if (!state || !batch) return null;

  const { bind, errors, busy } = form;
  const client = index.clientsById.get(batch.clientId);

  const validate = (x) => {
    const e = {};
    if (!x.name.trim()) e.name = 'Enter a batch name.';
    return e;
  };

  const onSubmit = (e) => form.submit(e, validate, (x) => {
    const patch = changedFields(form.initial, x);
    if (!Object.keys(patch).length) return close();
    // A moved batch takes its deadline along when it was the old month's end (the default), empty, or before the new
    // month — otherwise it would read overdue at once (LOGIC-01; same rule as Move Batch on the client page).
    if (Object.hasOwn(patch, 'monthNumber')) {
      const from = monthSpan(index, client, Number(form.initial.monthNumber));
      const to = monthSpan(index, client, Number(patch.monthNumber));
      const due = batch.dueDate || '';
      if (to?.endDate && (!due || due === from?.endDate || due < to.startDate)) patch.dueDate = to.endDate;
    }
    return dispatch('batch.update', { id: batch.id, patch }).then((result) => {
      const moved = Object.hasOwn(patch, 'monthNumber') ? ` Moved to ${monthLabel(result?.monthNumber || x.monthNumber)}.` : '';
      const fresh = getSnapshot().state;
      if (Object.hasOwn(patch, 'batchNumber') && fresh && batchNumberTaken(fresh, batch.clientId, patch.batchNumber, batch.id)) {
        duplicateToast(batchLabel(patch.batchNumber), `Batch saved.${moved}`);
      } else toast(`Batch saved.${moved}`, { tone: 'success' });
      close();
    });
  });

  return (
    <FormSheet
      title="Edit Batch"
      subtitle={client?.name}
      width={480}
      onClose={close}
      onSubmit={onSubmit}
      dirty={form.dirty}
      footer={(cancel) => <SheetFooter onCancel={cancel} busy={busy} submitLabel="Save" busyLabel="Saving…" />}
    >
      <Field label="Batch Name" required error={errors.name}>
        <TextInput {...bind('name')} maxLength={MAX.name} autoComplete="off" data-autofocus />
      </Field>
      <BatchNumbers form={form} state={state} index={index} clientId={batch.clientId} exceptId={batch.id} videos={false} />
    </FormSheet>
  );
}

// Context: { clientId, monthId } or { clientId, monthNumber } (a month not created yet is fine). The month is the
// Month no. slider's default; without a client context the sheet also asks for the client.
// options.onCreated({ batchId, monthId, monthNumber }) runs after a successful create.
export function openAddBatch(context, options) {
  const { clientId, monthId, monthNumber } = context && typeof context === 'object' && !('target' in context) ? context : {};
  const { index } = getSnapshot();
  let month = (monthId && index?.monthsById.get(monthId)) || null;
  const cid = month ? month.clientId : clientId;
  const valid = !!(cid && index?.clientsById.has(cid));
  if (!month && valid && Number(monthNumber) > 0) {
    month = (index.monthsByClient.get(cid) || []).find((m) => m.monthNumber === Number(monthNumber)) || null;
  }
  const n = Math.round(Number(monthNumber));
  const ctx = { clientId: valid ? cid : '', monthId: valid && month ? month.id : '', monthNumber: valid && !month && n >= 1 && n <= MAX_MONTH ? n : 0 };
  const onCreated = typeof options?.onCreated === 'function' ? options.onCreated : undefined;
  if (!ctx.clientId && !getSnapshot().state?.clients.length) return openDrawer((close) => <NeedClientSheet close={close} />);
  return openDrawer((close) => <AddBatchForm close={close} context={ctx} onCreated={onCreated} />);
}

// Add Batch with no clients yet: every batch belongs to a client, so the one step is New client (then Add Batch for it).
function NeedClientSheet({ close }) {
  const onSubmit = () => {
    close();
    // Batch 01 made by "Start Month 1 now" is the batch asked for: open the client instead of a second Add Batch.
    openAddClient({ onCreated: (id, result) => (result?.batchId ? navigate(clientHref(id).replace(/^#/, '')) : openAddBatch({ clientId: id })) });
  };
  return (
    <FormSheet
      title="Add Batch"
      onClose={close}
      onSubmit={onSubmit}
      footer={(cancel) => <SheetFooter onCancel={cancel} submitLabel="New client" />}
    >
      <p class="fm-first"><strong>Add a client first.</strong> Every batch belongs to a client — create one, then add its batch.</p>
    </FormSheet>
  );
}

// Accepts a batch object or its id.
export function openEditBatch(batch) {
  const id = typeof batch === 'string' ? batch : batch?.id;
  if (!id) return undefined;
  return openDrawer((close) => <EditBatchForm close={close} batchId={id} />);
}

// ---------- event ----------

// A batch's stage suggests the event type (QA-22).
const STAGE_KIND = {
  briefPremisScript: 'Script Deadline', script: 'Script Deadline', videoShootEditingBrief: 'Shoot Date',
  videoEditing: 'Editing Deadline', result: 'Result Deadline',
};
const EVENT_KEYS = ['title', 'start', 'end', 'allDay', 'calendarType', 'kind', 'clientId', 'monthId', 'batchId', 'stage', 'owner', 'notes'];

function plusHour(hhmm) {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || '');
  if (!m) return '10:00';
  const h = Number(m[1]) + 1;
  return h > 23 ? '23:59' : `${String(h).padStart(2, '0')}:${m[2]}`;
}

// Links as stored, each validated against the index (an edit never invents a month the event didn't have — QA-07).
function storedLinks(index, e) {
  const batch = e.batchId ? index.batchesById.get(e.batchId) : null;
  if (batch) return { clientId: batch.clientId, monthId: batch.monthId, batchId: batch.id };
  const month = e.monthId ? index.monthsById.get(e.monthId) : null;
  if (month) return { clientId: month.clientId, monthId: month.id, batchId: '' };
  return { clientId: e.clientId && index.clientsById.has(e.clientId) ? e.clientId : '', monthId: '', batchId: '' };
}

// New events linked to a client only default to its current month.
function newLinks(index, d, today) {
  const links = storedLinks(index, d);
  if (links.clientId && !links.monthId) links.monthId = activeMonthOf(index, links.clientId, today)?.id || '';
  return links;
}

function initialEvent(ev, d, index, today) {
  if (ev) {
    const startTime = timePart(ev.start) || '09:00';
    const endDate = ev.allDay && ev.end && datePart(ev.end) !== datePart(ev.start) ? datePart(ev.end) : '';
    return {
      title: ev.title, calendarType: ev.calendarType, kind: ev.kind, date: datePart(ev.start), allDay: !!ev.allDay,
      startTime, endTime: ev.allDay ? plusHour(startTime) : timePart(ev.end), endDate,
      ...storedLinks(index, ev),
      stage: ev.stage || '', owner: index.teamById.has(ev.owner) ? ev.owner : '', notes: ev.notes || '',
    };
  }
  const calendarType = Object.hasOwn(EVENT_KINDS, d.calendarType) ? d.calendarType : 'main';
  const links = newLinks(index, d, today);
  const batch = links.batchId ? index.batchesById.get(links.batchId) : null;
  const stage = STAGES.some((s) => s.key === d.stage) ? d.stage : batch?.currentStage || '';
  const kinds = EVENT_KINDS[calendarType];
  const suggested = calendarType === 'main' && STAGE_KIND[stage];
  const kind = kinds.includes(d.kind) ? d.kind : suggested || (calendarType === 'main' ? '' : kinds[0]);
  const rawStart = typeof d.start === 'string' ? d.start : '';
  const date = isISODate(datePart(rawStart)) ? datePart(rawStart) : today;
  const time = timePart(rawStart);
  // A date-only default (a month cell, or a deadline for a batch) is all day; a slot click keeps its time.
  let allDay = d.allDay != null ? !!d.allDay : !!rawStart && !time;
  if (d.allDay == null && !time && /Deadline$/.test(kind)) allDay = true;
  const startTime = time || '09:00';
  const endTime = timePart(d.end || '') || plusHour(startTime);
  const endDate = allDay && d.end && datePart(d.end) > date ? datePart(d.end) : '';
  return {
    title: d.title || '', calendarType, kind, date, allDay, startTime, endTime, endDate, ...links, stage,
    owner: d.owner && index.teamById.has(d.owner) ? d.owner : '', notes: d.notes || '',
  };
}

// Start/end in the ARCHITECTURE §5 formats: 'YYYY-MM-DD' when all day, else 'YYYY-MM-DDTHH:mm'. A timed event whose
// end time is before its start ends the next day (QA-08: an overnight shoot stays editable).
function eventPayload(x) {
  const start = x.allDay ? x.date : `${x.date}T${x.startTime}`;
  let end = '';
  if (x.allDay) end = x.endDate && x.endDate > x.date ? x.endDate : '';
  else if (x.endTime) end = `${x.endTime < x.startTime ? addDays(x.date, 1) : x.date}T${x.endTime}`;
  return {
    title: x.title, start, end, allDay: x.allDay, calendarType: x.calendarType, kind: x.kind,
    clientId: x.clientId, monthId: x.monthId, batchId: x.batchId, stage: x.stage, owner: x.owner, notes: x.notes,
  };
}

function EventForm({ close, eventId, defaults }) {
  const { state, index, today } = useStore();
  const editing = !!eventId;
  const stored = editing ? index?.eventsById.get(eventId) : null;
  const form = useForm(() => initialEvent(stored, defaults || {}, index, today));
  const [linksOpen, setLinksOpen] = useState(() => form.initial.calendarType === 'main' || !!form.initial.clientId);
  useCloseWhenMissing(editing, stored, close);
  if (!state || (editing && !stored)) return null;

  const { values: v, bind, errors, busy } = form;
  const kinds = EVENT_KINDS[v.calendarType] || EVENT_KINDS.main;
  const linkedClient = index.clientsById.get(v.clientId);
  const activeN = linkedClient ? activeMonthNumber(linkedClient, today) : 0;
  const monthBatches = v.monthId ? index.batchesByMonth.get(v.monthId) || [] : [];
  const monthOptions = (index.monthsByClient.get(v.clientId) || []).map((m) => ({
    value: m.id,
    label: `${monthLabel(m.monthNumber)}${m.monthNumber === activeN ? ' · Current' : ''}`,
  }));
  const batchOptions = monthBatches.map((b) => ({ value: b.id, label: `${batchLabel(b.batchNumber)} · ${b.name}` }));
  const stageOptions = STAGES.map((s) => ({ value: s.key, label: `${s.number} ${s.label}` }));

  const onCalendar = (t) => form.set({ calendarType: t, kind: EVENT_KINDS[t].includes(v.kind) ? v.kind : t === 'main' ? '' : EVENT_KINDS[t][0] });
  const onClient = (id) => form.set({ clientId: id, monthId: id ? activeMonthOf(index, id, today)?.id || '' : '', batchId: '' });
  const onMonth = (id) => form.set({ monthId: id, batchId: '' });
  const onBatch = (id) => {
    const b = index.batchesById.get(id);
    form.set({ batchId: id, ...(b && !v.stage ? { stage: b.currentStage } : {}) });
  };

  const validate = (x) => {
    const e = {};
    if (!x.title.trim()) e.title = 'Enter a title.';
    if (!x.date) e.date = 'Choose a date.';
    else if (!isISODate(x.date)) e.date = 'Enter a valid date.';
    if (x.allDay) {
      if (x.endDate && isISODate(x.date) && x.endDate < x.date) e.endDate = 'Ends before the date.';
    } else if (!x.startTime) e.startTime = 'Choose a time.';
    return e;
  };

  const onSubmit = (e) => form.submit(e, validate, (x) => {
    if (editing) {
      const patch = changedFields(eventPayload(form.initial), eventPayload(x), EVENT_KEYS);
      if (!Object.keys(patch).length) return close();
      return dispatch('event.update', { id: stored.id, patch }).then(() => {
        toast('Event saved.', { tone: 'success' });
        close();
      });
    }
    return dispatch('event.create', eventPayload(x)).then(() => {
      toast(`Event created for ${formatDate(x.date, 'weekday')}.`, { tone: 'success' });
      close();
    });
  });

  const onDelete = () => {
    form.setBusy(true);
    dispatch('event.delete', { id: stored.id })
      .then(() => {
        toast('Event deleted.');
        close();
      })
      .catch(() => form.setBusy(false));
  };
  const remove = editing ? { label: 'Delete', prompt: 'Delete this event? This can’t be undone.', run: onDelete } : undefined;
  const overnight = !v.allDay && v.startTime && v.endTime && v.endTime < v.startTime;

  const links = (
    <>
      <Field label="Client">
        <Pick name="clientId" value={v.clientId} options={clientOptionsOf(state)} placeholder="No client" onValue={onClient} />
      </Field>
      <div class="fm-grid fm-link-grid">
        <Field label="Month">
          <Pick name="monthId" value={v.monthId} options={monthOptions} placeholder="No month" disabled={!v.clientId} onValue={onMonth} />
        </Field>
        <Field label="Batch">
          <Pick name="batchId" value={v.batchId} options={batchOptions} placeholder={v.monthId && !monthBatches.length ? 'No batches yet' : 'No batch'} disabled={!v.monthId || !monthBatches.length} onValue={onBatch} />
        </Field>
        <Field label="Stage">
          <Pick {...bind('stage')} options={stageOptions} placeholder="No stage" />
        </Field>
        <Field label="Owner">
          <Pick {...bind('owner')} options={activeTeamOptions(state.team, form.initial.owner)} placeholder="Unassigned" />
        </Field>
      </div>
    </>
  );

  return (
    <FormSheet
      title={editing ? 'Edit event' : 'New event'}
      onClose={close}
      onSubmit={onSubmit}
      dirty={form.dirty || (!editing && !!form.initial.title)}
      width={460}
      footer={(cancel) => <SheetFooter onCancel={cancel} busy={busy} remove={remove} submitLabel={editing ? 'Save' : 'Create'} busyLabel={editing ? 'Saving…' : 'Creating…'} />}
    >
      <Field label="Title" required error={errors.title}>
        <TextInput {...bind('title')} maxLength={MAX.name} placeholder="e.g. Shoot Date: Morning Ritual Series" autoComplete="off" data-autofocus />
      </Field>
      <Field as="div" label="Calendar">
        <FilterButtons options={CALENDAR_OPTIONS} value={v.calendarType} onChange={onCalendar} label="Calendar" size="sm" class={cx('fm-cal', `is-${v.calendarType}`)} />
      </Field>
      <Field label="Type">
        <Pick {...bind('kind')} options={kinds} placeholder={v.calendarType === 'main' ? 'General' : undefined} />
      </Field>
      <div class={cx('fm-when', v.allDay && 'is-allday')}>
        <Field label="Date" required error={errors.date} class="fm-date">
          <DateInput {...bind('date')} />
        </Field>
        {v.allDay ? (
          <Field label="Until" error={errors.endDate} class="fm-until">
            <DateInput {...bind('endDate')} min={v.date || undefined} />
          </Field>
        ) : (
          <>
            <Field label="Start" required error={errors.startTime}>
              <TextInput {...bind('startTime')} type="time" step="300" />
            </Field>
            <Field label="End" hint={overnight ? 'Next day' : undefined}>
              <TextInput {...bind('endTime')} type="time" step="300" />
            </Field>
          </>
        )}
      </div>
      <label class="fm-check">
        <input type="checkbox" name="allDay" checked={v.allDay} onChange={(e) => form.set({ allDay: e.currentTarget.checked })} />
        All day
      </label>
      {v.calendarType === 'main' ? <div class="fm-section"><p class="fm-section-label">Link to production</p>{links}</div> : (
        <MoreOptions open={linksOpen} onToggle={() => setLinksOpen(!linksOpen)} label="Link to production">{links}</MoreOptions>
      )}
      <Field label="Notes">
        <TextArea {...bind('notes')} rows={3} maxLength={MAX.notes} placeholder="Location, agenda, anything to prepare" onKeyDown={submitOnModEnter} />
      </Field>
    </FormSheet>
  );
}

// event: a stored event, or a calendar item ({ eventId }). A derived item (a batch due date) opens a new event
// pre-linked to that batch. defaults: { start: 'YYYY-MM-DD' | 'YYYY-MM-DDTHH:mm', end, allDay, title, kind,
// clientId, monthId, batchId, stage, owner, calendarType, notes } — e.g. what was typed in the calendar's quick-create
// popover ("More options"); a carried-over title counts as typed input for the discard guard.
export function openEvent(arg) {
  const { event, defaults } = arg && typeof arg === 'object' && !('target' in arg) ? arg : {};
  const { index } = getSnapshot();
  let eventId = '';
  let d = { ...(defaults || {}) };
  if (event) {
    const stored = index?.eventsById.get(event.eventId || event.id);
    if (stored) eventId = stored.id;
    else {
      d = {
        start: event.start, calendarType: event.calendarType, clientId: event.clientId, monthId: event.monthId,
        batchId: event.batchId, stage: event.stage, owner: event.owner, ...d,
      };
    }
  }
  return openDrawer((close) => <EventForm close={close} eventId={eventId} defaults={d} />);
}

// ---------- team member ----------

// Add member (admins, DESIGN §8): Name · Login ID (follows the first name until edited) · Department · Role ·
// Initial password (server only; blank = the team's initial password). Edit: the same minus the password.
function TeamForm({ close, memberId, onSaved, onRemoved }) {
  const snap = useStore();
  const { state, index } = snap;
  const editing = !!memberId;
  const member = editing ? index?.teamById.get(memberId) : null;
  const accounts = MODE === 'server' && snap.authRequired !== false; // passwords exist only on a server with sign-in
  const form = useForm(() => ({
    name: member?.name || '', loginId: member?.loginId || '', department: member?.department || '', role: member?.role || '', password: '',
  }));
  const [idEdited, setIdEdited] = useState(editing);
  useCloseWhenMissing(editing, member, close);
  if (!state || (editing && !member)) return null;

  const { values, bind, errors, busy, set } = form;
  const others = state.team.filter((t) => t.id !== memberId);
  const takenIds = new Set(others.map((t) => String(t.loginId || '').toLowerCase()).filter(Boolean));
  const suggestId = (name) => {
    const base = loginSlug(name);
    if (base.length < 2) return '';
    let id = base;
    for (let n = 2; takenIds.has(id); n++) id = `${base}${n}`;
    return id;
  };
  const cleanId = (v) => String(v || '').trim().toLowerCase();
  const roles = choiceOptions(TEAM_ROLES, state.team.map((t) => t.role), values.role);
  const departments = choiceOptions(DEPARTMENTS, state.team.map((t) => t.department), values.department);

  const validate = (x) => {
    const e = {};
    if (!x.name.trim()) e.name = 'Enter a name.';
    const id = cleanId(x.loginId);
    if (!editing || id !== cleanId(form.initial.loginId)) {
      if (!id) e.loginId = 'Enter a login ID.';
      else if (!LOGIN_ID_PATTERN.test(id)) e.loginId = 'Use 2–40 lowercase letters, digits, dots, dashes or underscores.';
      else if (takenIds.has(id)) e.loginId = 'Another member already uses this login ID.';
    }
    if (x.password && x.password.length < PASSWORD_MIN) e.password = `Use at least ${PASSWORD_MIN} characters.`;
    else if (x.password.length > PASSWORD_MAX) e.password = `Use at most ${PASSWORD_MAX} characters.`;
    return e;
  };
  const onSubmit = (e) => form.submit(e, validate, (x) => {
    const fields = { name: x.name, loginId: cleanId(x.loginId), department: x.department, role: x.role };
    if (editing) {
      const patch = changedFields({ ...form.initial, loginId: cleanId(form.initial.loginId) }, fields, ['name', 'loginId', 'department', 'role']);
      if (!Object.keys(patch).length) return close();
      return dispatch('team.update', { id: member.id, patch }).then(() => {
        toast(`${x.name.trim()} saved.`, { tone: 'success' });
        close();
        onSaved?.(member.id);
      });
    }
    const payload = accounts && x.password ? { ...fields, password: x.password } : fields;
    return dispatch('team.create', payload).then((result) => {
      toast(`${x.name.trim()} added. They sign in as “${fields.loginId}”.`, { tone: 'success' });
      close();
      onSaved?.(result?.teamMemberId);
    });
  });

  let remove;
  if (editing) {
    remove = {
      label: 'Remove',
      prompt: removalText(state, member, accounts), // same words as the People row menu
      run: () => {
        form.setBusy(true);
        dispatch('team.delete', { id: member.id })
          .then(() => {
            toast(`${member.name} removed.`);
            close();
            if (onRemoved) onRemoved(member.id);
            else {
              const route = getRoute();
              if (route.name === 'person' && route.params.memberId === member.id) navigate('/people', { replace: true });
            }
          })
          .catch(() => form.setBusy(false));
      },
    };
  }

  return (
    <FormSheet
      title={editing ? 'Edit member' : 'Add member'}
      onClose={close}
      onSubmit={onSubmit}
      dirty={form.dirty}
      width={380}
      footer={(cancel) => <SheetFooter onCancel={cancel} busy={busy} remove={remove} submitLabel={editing ? 'Save' : 'Add'} busyLabel={editing ? 'Saving…' : 'Adding…'} />}
    >
      <Field label="Name" required error={errors.name}>
        <TextInput {...bind('name')} onValue={(v) => set(idEdited ? { name: v } : { name: v, loginId: suggestId(v) })}
          maxLength={MAX.name} placeholder="Full name" autoComplete="off" data-autofocus />
      </Field>
      <Field label="Login ID" required error={errors.loginId} hint={editing ? 'Used to sign in, with their password.' : 'Their first name, lowercase. Used to sign in.'}>
        <TextInput {...bind('loginId')} onValue={(v) => { setIdEdited(true); set({ loginId: v.toLowerCase().replace(/\s+/g, '') }); }}
          class="login-id" maxLength={40} placeholder="e.g. ady" autoComplete="off" autoCapitalize="none" spellCheck={false} />
      </Field>
      <div class="fm-grid">
        <Field label="Department">
          <ChoiceSelect name="department" value={values.department} options={departments} onValue={(v) => set({ department: v })}
            placeholder="No department" customPlaceholder="Department" maxLength={MAX.name} />
        </Field>
        <Field label="Role">
          <ChoiceSelect name="role" value={values.role} options={roles} onValue={(v) => set({ role: v })}
            placeholder="No role" customPlaceholder="Role" maxLength={MAX.name} />
        </Field>
      </div>
      {accounts && !editing && (
        <Field label="Initial password" error={errors.password} hint="Optional. Leave blank to use the team’s initial password.">
          <TextInput {...bind('password')} type="text" autoComplete="off" spellCheck={false} autoCapitalize="off" maxLength={PASSWORD_MAX} />
        </Field>
      )}
    </FormSheet>
  );
}

// member: a team member object or id (omit to add). options.onSaved(id) after a save; options.onRemoved(id) after
// "Remove" (edit only — e.g. navigate away from that person's page).
export function openTeamMember(member, options) {
  const id = typeof member === 'string' ? member : (member && typeof member === 'object' && typeof member.id === 'string' ? member.id : '');
  const onSaved = typeof options?.onSaved === 'function' ? options.onSaved : undefined;
  const onRemoved = typeof options?.onRemoved === 'function' ? options.onRemoved : undefined;
  return openDrawer((close) => <TeamForm close={close} memberId={id} onSaved={onSaved} onRemoved={onRemoved} />);
}
